import React, { useState, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAttendance } from '../../contexts/AttendanceContext';
import { useLanguage } from '../../contexts/LanguageContext';

import { Avatar, AvatarFallback } from '../ui/avatar';
import PageTransition from '../PageTransition';
import BottomNav from './BottomNav';
import CameraCapture from '../CameraCapture';
import PullToRefresh from '../PullToRefresh';
import { toast } from 'sonner';
import { Settings, LogOut, AlertCircle } from 'lucide-react';
import { storage } from '../../utils/storage';
import { getEarlyLeaveMinutes, getEffectiveEndTime, getMinutesUntilShiftStart } from '../../utils/dateFormatter';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
} from '../ui/alert-dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export default function Layout(): React.JSX.Element {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { t } = useLanguage();
    const { todayStatus, checkIn, checkOut } = useAttendance();
    const [showCamera, setShowCamera] = useState(false);
    const [cameraMode, setCameraMode] = useState<'check_in' | 'check_out'>('check_in');

    // Early Checkout state
    const [showEarlyCheckoutModal, setShowEarlyCheckoutModal] = useState(false);
    const [earlyLeaveLabel, setEarlyLeaveLabel] = useState('');

    // Too Early to Check-In state
    const [showTooEarlyModal, setShowTooEarlyModal] = useState(false);

    const getInitials = (name: string): string =>
        name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

    const handleCameraFab = (): void => {
        if (todayStatus?.clockIn && !todayStatus?.clockOut) {
            setCameraMode('check_out');

            // Check for early check-out
            const shiftSettings = storage.getShiftSettings();
            const effectiveEndTime = getEffectiveEndTime(shiftSettings);
            if (shiftSettings.startTime && effectiveEndTime) {
                const earlyMinutes = getEarlyLeaveMinutes(shiftSettings.startTime, effectiveEndTime);
                if (earlyMinutes > 0) {
                    setEarlyLeaveLabel(
                        earlyMinutes >= 60
                            ? `${Math.floor(earlyMinutes / 60)}h ${earlyMinutes % 60}m`
                            : `${earlyMinutes} min`
                    );
                    setShowEarlyCheckoutModal(true);
                    return;
                }
            }
        } else {
            setCameraMode('check_in');

            // Guard: too early to check in (>60 min before shift start)
            const shiftSettings = storage.getShiftSettings();
            const effectiveEndTime = getEffectiveEndTime(shiftSettings);
            if (shiftSettings.startTime) {
                const mins = getMinutesUntilShiftStart(shiftSettings.startTime, effectiveEndTime);
                if (mins > 60) {
                    setShowTooEarlyModal(true);
                    return;
                }
            }
        }
        setShowCamera(true);
    };

    const handleCameraCapture = async (photoDataUrl: string): Promise<void> => {
        setShowCamera(false);
        try {
            if (cameraMode === 'check_out') {
                await checkOut(photoDataUrl);
                toast.success(t.checkedOutToast, { description: t.checkedOutDesc });
            } else {
                await checkIn(photoDataUrl);
                toast.success(t.checkedInToast, { description: t.checkedInDesc });
            }
        } catch (error) {
            const key = cameraMode === 'check_out' ? t.checkOutFailed : t.checkInFailed;
            toast.error(key, { description: (error as Error).message });
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            toast.error(t.error || 'Terjadi kesalahan');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-subtle">
            <PullToRefresh />

            {/* ── Slim top header — logo + user info only ─────────────────────── */}
            <header
                className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80"
                style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
            >
                <div className="flex h-14 items-center justify-between px-4 max-w-2xl mx-auto">
                    <div className="flex items-center">
                        <img src="/logo-only.png" alt="Hadir" className="h-12 w-12 object-contain" />
                        <span className="text-base font-bold text-foreground tracking-tight">Hadir</span>
                    </div>

                    <DropdownMenu>
                        <DropdownMenuTrigger className="flex items-center gap-2 outline-none rounded-full transition-transform active:scale-95 focus-visible:ring-2 focus-visible:ring-primary group">
                            <Avatar className="h-8 w-8 cursor-pointer group-hover:opacity-80 transition-opacity">
                                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold shadow-sm">
                                    {getInitials(user?.fullName ?? 'U')}
                                </AvatarFallback>
                            </Avatar>
                            <div className="hidden sm:block text-left">
                                <p className="text-sm font-medium text-foreground leading-tight group-hover:text-primary transition-colors">{user?.fullName}</p>
                                <p className="text-xs text-muted-foreground">{user?.code}</p>
                            </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 mt-1 rounded-xl shadow-lg border-border/60 animate-in fade-in zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95 duration-200">
                            <DropdownMenuLabel className="font-bold">{t.myAccount || "Akun Saya"}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="cursor-pointer font-medium py-2.5 rounded-lg focus:bg-primary/10 focus:text-primary transition-colors" onClick={() => navigate('/settings')}>
                                <Settings className="mr-2 h-4 w-4" />
                                <span>{t.settings || "Pengaturan"}</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem className="cursor-pointer font-medium py-2.5 rounded-lg text-destructive focus:bg-destructive/10 focus:text-destructive transition-colors" onClick={() => void handleLogout()}>
                                <LogOut className="mr-2 h-4 w-4" />
                                <span>{t.logout || "Keluar"}</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            {/* ── Main content ────────────────────────────────────────────── */}
            <main
                className="mx-auto px-4 pt-4 max-w-2xl"
                style={{ paddingBottom: 'calc(4.75rem + env(safe-area-inset-bottom, 0px))' }}
            >
                <PageTransition>
                    <Outlet />
                </PageTransition>
            </main>

            {/* ── Fixed overlays — outside scroll container ─────────────────── */}
            <BottomNav onCameraFab={handleCameraFab} />

            <CameraCapture
                open={showCamera}
                onCapture={(photo) => void handleCameraCapture(photo)}
                onClose={() => setShowCamera(false)}
                title={cameraMode === 'check_out' ? t.checkOutCameraTitle : t.cameraTitle}
            />

            {/* ── Early Check-Out Modal ── */}
            <AlertDialog open={showEarlyCheckoutModal} onOpenChange={setShowEarlyCheckoutModal}>
                <AlertDialogContent className="max-w-sm p-0 overflow-hidden rounded-3xl border-0 shadow-2xl">
                    <div
                        className="relative overflow-hidden px-5 py-5 premium-overlay"
                        style={{ background: 'linear-gradient(135deg, hsl(var(--warning)) 0%, hsl(35 90% 42%) 100%)' }}
                    >
                        <div className="relative flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 shadow-inner flex-shrink-0">
                                <AlertCircle className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <p className="text-[11px] font-bold text-white/70 uppercase tracking-[0.15em]">ABSENSI</p>
                                <p className="text-[15px] font-black text-white leading-tight">{t.earlyCheckout}</p>
                            </div>
                        </div>
                    </div>
                    <div className="px-5 py-5 space-y-3">
                        <p className="text-sm text-muted-foreground">{t.earlyCheckoutDesc(getEffectiveEndTime(storage.getShiftSettings()) || '', earlyLeaveLabel)}</p>
                        <div className="p-3.5 rounded-xl border" style={{ background: 'hsl(var(--warning) / 0.08)', borderColor: 'hsl(var(--warning) / 0.25)' }}>
                            <p className="text-xs font-semibold" style={{ color: 'hsl(var(--warning))' }}>{t.earlyCheckoutWarning}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">{t.earlyCheckoutConfirm}</p>
                        <div className="flex flex-col gap-2.5 pt-1">
                            <AlertDialogAction
                                onClick={() => { setShowEarlyCheckoutModal(false); setShowCamera(true); }}
                                className="w-full h-11 rounded-xl font-bold inline-flex items-center justify-center gap-2"
                                style={{ background: 'linear-gradient(135deg, hsl(var(--warning)) 0%, hsl(35 90% 42%) 100%)', color: '#fff' }}
                            >
                                <LogOut className="h-4 w-4" />
                                {t.checkOutEarly}
                            </AlertDialogAction>
                            <AlertDialogCancel className="w-full rounded-xl h-11">{t.stayWorking}</AlertDialogCancel>
                        </div>
                    </div>
                </AlertDialogContent>
            </AlertDialog>

            {/* ── Too Early to Check-In Modal ── */}
            <AlertDialog open={showTooEarlyModal} onOpenChange={setShowTooEarlyModal}>
                <AlertDialogContent className="max-w-sm p-0 overflow-hidden rounded-3xl border-0 shadow-2xl">
                    <div
                        className="relative overflow-hidden px-5 py-5 premium-overlay"
                        style={{ background: 'linear-gradient(135deg, hsl(var(--warning)) 0%, hsl(35 90% 42%) 100%)' }}
                    >
                        <div className="relative flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 shadow-inner flex-shrink-0">
                                <AlertCircle className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <p className="text-[11px] font-bold text-white/70 uppercase tracking-[0.15em]">ABSENSI</p>
                                <p className="text-[15px] font-black text-white leading-tight">{t.tooEarlyTitle}</p>
                            </div>
                        </div>
                    </div>
                    <div className="px-5 py-5 space-y-3">
                        <p className="text-sm text-muted-foreground">{t.tooEarlyDesc}</p>
                        <AlertDialogAction
                            onClick={() => setShowTooEarlyModal(false)}
                            className="w-full h-11 rounded-xl font-bold inline-flex items-center justify-center"
                            style={{ background: 'var(--gradient-primary)', color: '#fff' }}
                        >
                            {t.tooEarlyGotIt}
                        </AlertDialogAction>
                    </div>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
